import type { McpServer, RemoteAgent } from '@ralph/contracts'
import { AlertTriangle, ArrowLeft, PauseCircle, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Notice, riseDelay, SectionHead, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Panel, Readout, Spinner } from '@/components/ui/primitives.tsx'
import {
  useDeleteSystem,
  useProbeSystem,
  useRemoteAgents,
  useSystem,
  useSystems,
  useUpdateRemoteAgent,
  useUpdateSystem
} from '@/hooks/use-systems.ts'
import { describeIssue, useI18n, useT } from '@/i18n/index.ts'
import { CapabilityReview } from './CapabilityReview.tsx'
import { ConnectFlow } from './ConnectFlow.tsx'
import { StageLadder } from './StageLadder.tsx'
import { AccessSummary, SystemAccessEditor, SystemAccessPanel } from './SystemAccess.tsx'
import { probeStatusLabel } from './system-labels.ts'

/**
 * The studio tab for everything this platform may reach.
 *
 * Three views on one tab rather than three routes: the list, the connect flow, and one
 * system. The studio is already a tabbed surface inside a page, and giving a sub-view its
 * own URL would put a second navigation model inside the first — a reader who pressed the
 * browser's back button from a capability review would land outside the studio entirely.
 *
 * It sits **before** the "Capabilities" tab in the tab bar, which is not cosmetic ordering.
 * That tab is the wording backlog for capabilities that have already been discovered and
 * switched on; on a fresh deployment it is empty and nothing in the product could ever fill
 * it. This is the tab that fills it.
 *
 * Remote agents are the tab's second section (PRD-26 GRP-061): the other kind of outside system
 * an agent may reach, limited to groups the same way, with the same words and the same picker.
 */

type View = { kind: 'list' } | { kind: 'connect' } | { kind: 'system'; id: string }

/**
 * What the browser was told on its way back from an authority.
 *
 * Read once, then removed from the address bar — so a refresh does not re-announce a sign-in
 * that happened ten minutes ago, and so a failure reason (which is text a third party
 * partly chose) does not sit in a URL somebody might paste into a ticket.
 */
const useReturnNotice = (): { connected: string | null; failed: string | null } => {
  const [notice] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return { connected: params.get('connected'), failed: params.get('connectFailed') }
  })

  useEffect(() => {
    if (!notice.connected && !notice.failed) return
    const url = new URL(window.location.href)
    url.searchParams.delete('connected')
    url.searchParams.delete('connectFailed')
    // `tab` is left alone: the studio reads it once at mount, and removing it here would be
    // harmless today and a puzzle the first time somebody makes the tab state URL-driven.
    window.history.replaceState({}, '', url.toString())
  }, [notice])

  return notice
}

export const ConnectedSystemsPanel = ({ enabled }: { enabled: boolean }) => {
  const t = useT().admin.systems
  const systems = useSystems(enabled)
  const [view, setView] = useState<View>({ kind: 'list' })
  const returned = useReturnNotice()

  if (view.kind === 'connect') {
    return <ConnectFlow onDone={(id) => setView({ kind: 'system', id })} onCancel={() => setView({ kind: 'list' })} />
  }

  if (view.kind === 'system') {
    return <SystemDetail id={view.id} onBack={() => setView({ kind: 'list' })} />
  }

  const items = systems.data?.items ?? []

  return (
    <section>
      <SectionHead
        title={t.title}
        lead={t.lead}
        action={
          <Button size="sm" variant="primary" onClick={() => setView({ kind: 'connect' })}>
            <Plus size={13} strokeWidth={2.4} aria-hidden />
            {t.connect}
          </Button>
        }
      />

      {returned.connected && (
        <p
          role="status"
          className="mt-3 rounded-tile border border-grounded/40 bg-grounded/[0.08] px-3 py-2.5 text-[13px] text-grounded"
        >
          {t.flow.returnedOk(returned.connected)}
        </p>
      )}

      {returned.failed && (
        <div className="mt-3">
          <Notice title={t.flow.returnedFailedTitle} icon={<AlertTriangle size={13} strokeWidth={2.1} />} role="alert">
            {returned.failed}
          </Notice>
        </div>
      )}

      {systems.isLoading && (
        <p className="mt-3 flex items-center gap-2 text-[13px] text-fg-subtle">
          <Spinner /> {t.loading}
        </p>
      )}

      {systems.isError && (
        <div className="mt-3">
          <EmptyState title={t.unavailable} body={t.unavailableBody} />
        </div>
      )}

      {!systems.isLoading && !systems.isError && items.length === 0 && (
        <div className="mt-3">
          <EmptyState title={t.empty} body={t.emptyBody} />
        </div>
      )}

      <ul className="mt-4 space-y-2.5">
        {items.map((system, index) => (
          <li key={system.id}>
            <SystemRow system={system} index={index} onOpen={() => setView({ kind: 'system', id: system.id })} />
          </li>
        ))}
      </ul>

      <RemoteAgentsSection enabled={enabled} />
    </section>
  )
}

/**
 * One system, at a glance.
 *
 * The health line is the point of the row, and it is written as a sentence rather than as a
 * status pill: "checked 2 hours ago" and "never checked" are the two states an administrator
 * actually acts on, and a green dot says neither. A paused system says what pausing means
 * rather than only that it is paused — the fear on that control is "have I lost the review
 * work", and the answer is no.
 */
const SystemRow = ({ system, index, onOpen }: { system: McpServer; index: number; onOpen: () => void }) => {
  // Bound inside the component, never captured at module scope (PRD-27 F-3). The words go
  // the same way as the formatters: `const t = systemsStrings` at the top of this file was
  // the capture that renders the previous language after a switch, silently.
  const fmt = useI18n()
  const t = fmt.t.admin.systems
  const unhealthy = system.lastProbeStatus !== null && system.lastProbeStatus !== 'ok'
  const title = String(system.serverInfo.name ?? '') || system.name

  return (
    <StudioRow style={riseDelay(index)}>
      <div className="flex flex-wrap items-start gap-x-2.5 gap-y-1.5">
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px]">
            {title}
            {/* The name it was registered under, when the system calls itself something else. */}
            {title !== system.name && <span className="readout ml-1.5 text-fg-subtle"> {system.name}</span>}
          </h3>
          {system.description !== '' && (
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-fg-muted">{system.description}</p>
          )}
        </div>
        {!system.enabled && <Badge tone="alert">{t.disabled}</Badge>}
        {/* `lastProbeStatus` is a bare string on the wire, so the badge asks for the guarded
            label rather than casting the widening away — which is what `as never` did, and how
            an unlisted status produced a red badge with nothing written in it. */}
        {unhealthy && system.enabled && <Badge tone="alert">{probeStatusLabel(t, system.lastProbeStatus)}</Badge>}
        <Button size="sm" aria-label={t.openLabel(title)} onClick={onOpen}>
          {t.open}
        </Button>
      </div>

      <p className="mt-2 text-[12.5px] leading-snug text-fg-subtle">
        {t.capabilityCount(fmt, system.liveCapabilityCount, system.capabilityCount)}
        <span aria-hidden className="mx-1.5">
          ·
        </span>
        {system.lastProbeAt ? t.checked(fmt.formatAgo(system.lastProbeAt)) : t.never}
      </p>

      <p className="mt-1.5 text-[12.5px] leading-snug text-fg-muted">
        <span className="sr-only">{t.access.label} </span>
        <AccessSummary access={system.access} />
      </p>

      {!system.enabled && <p className="mt-1 text-[12.5px] leading-snug text-fg-muted">{t.disabledHint}</p>}
    </StudioRow>
  )
}

const SystemDetail = ({ id, onBack }: { id: string; onBack: () => void }) => {
  const { formatAgo, t: messages } = useI18n()
  const t = messages.admin.systems
  const system = useSystem(id)
  const probe = useProbeSystem(id)
  const update = useUpdateSystem(id)
  const remove = useDeleteSystem()
  const [confirming, setConfirming] = useState<'pause' | 'remove' | null>(null)

  const row = system.data

  if (system.isLoading) {
    return (
      <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
        <Spinner /> {t.loading}
      </p>
    )
  }

  if (!row) {
    return (
      <EmptyState
        title={t.unavailable}
        body={t.unavailableBody}
        action={<Button onClick={onBack}>{t.flow.back}</Button>}
      />
    )
  }

  const displayName = String(row.serverInfo.name ?? '') || row.name
  const alsoOffers = Object.keys(row.capabilities).filter((key) => key !== 'tools' && key !== 'experimental')

  return (
    <section className="space-y-4">
      <SectionHead
        title={displayName}
        lead={row.description || row.url}
        action={
          <>
            <Button size="sm" onClick={onBack}>
              <ArrowLeft size={13} strokeWidth={2.1} aria-hidden />
              {t.title}
            </Button>
            <Button size="sm" disabled={probe.isPending} onClick={() => probe.mutate()}>
              {probe.isPending ? t.detail.diagnosing : t.detail.diagnose}
            </Button>
          </>
        }
      />

      {probe.data && (
        <>
          <StageLadder result={probe.data} url={row.url} />
          {!probe.data.ok && probe.data.hint !== '' && (
            <Notice
              title={t.probeStage[probe.data.stage ?? 'connect']}
              icon={<AlertTriangle size={13} strokeWidth={2.1} />}
            >
              {/* The same probe hint `ConnectFlow` renders, and it has to be read the same way:
                  the advice is ours and is keyed, while `detail` — the far side's own words —
                  stays a verbatim passthrough. Rendering it raw here left one of the two
                  screens that show a probe in English on a Portuguese deployment. */}
              {describeIssue(messages, { messageKey: probe.data.hintKey, params: probe.data.hintParams }) ??
                probe.data.hint}
            </Notice>
          )}
        </>
      )}

      <Panel className="px-4 py-3.5">
        <h3 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{t.detail.connectionTitle}</h3>
        <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
          <Readout label={t.detail.urlLabel} value={row.url} />
          <Readout
            label={t.detail.protocolLabel}
            value={
              row.protocolVersion
                ? `${row.protocolVersion} · ${row.era === 'legacy' ? t.detail.eraLegacy : t.detail.eraModern}`
                : '—'
            }
          />
          <Readout label={t.detail.modeLabel} value={row.tokenRef ?? t.noCredential} />
          <Readout
            label={t.detail.lastCheckedLabel}
            value={row.lastProbeAt ? formatAgo(row.lastProbeAt) : t.never}
            tone={row.lastProbeStatus !== null && row.lastProbeStatus !== 'ok' ? 'alert' : undefined}
          />
        </div>
        <p className="mt-2 text-[12.5px] leading-snug text-fg-subtle">{t.detail.protocolHint}</p>

        {alsoOffers.length > 0 && (
          <div className="mt-3">
            <p className="eyebrow">{t.detail.offersLabel}</p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">{alsoOffers.join(', ')}</p>
            <p className="mt-0.5 text-[12px] leading-snug text-fg-subtle">{t.detail.offersHint}</p>
          </div>
        )}

        <div className="mt-3.5 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={update.isPending}
            onClick={() =>
              row.enabled ? setConfirming('pause') : update.mutate({ version: row.version, patch: { enabled: true } })
            }
          >
            <PauseCircle size={13} strokeWidth={2.1} aria-hidden />
            {row.enabled ? t.detail.pause : t.detail.resume}
          </Button>
          <Button size="sm" disabled={remove.isPending} onClick={() => setConfirming('remove')}>
            {t.detail.remove}
          </Button>
        </div>

        {confirming && (
          <div className="mt-3">
            <Notice
              title={confirming === 'pause' ? t.detail.pauseConfirm : t.detail.removeConfirm}
              icon={<AlertTriangle size={13} strokeWidth={2.1} />}
            >
              {confirming === 'pause' ? t.detail.pauseBody : t.detail.removeBody}
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    if (confirming === 'pause') update.mutate({ version: row.version, patch: { enabled: false } })
                    else remove.mutate(row.id, { onSuccess: onBack })
                    setConfirming(null)
                  }}
                >
                  {confirming === 'pause' ? t.detail.pause : t.detail.remove}
                </Button>
                <Button size="sm" onClick={() => setConfirming(null)}>
                  {t.flow.cancel}
                </Button>
              </div>
            </Notice>
          </div>
        )}
      </Panel>

      <SystemAccessPanel
        name={displayName}
        access={row.access}
        lead={t.access.lead}
        saving={update.isPending}
        onSave={(access, handlers) => update.mutate({ version: row.version, patch: { access } }, handlers)}
        onStale={() => void system.refetch()}
      />

      {row.instructions !== '' && (
        <Panel className="px-4 py-3.5">
          <h3 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{t.detail.instructionsTitle}</h3>
          {/* Quoted, and never given to an agent — it is guidance a remote party wrote for a
              model, which is the definition of the thing this platform will not put in a
              prompt. Shown so an administrator knows it exists. */}
          <blockquote className="mt-2 rounded-r-tile border-l-2 border-rule-strong bg-fg/[0.03] py-2.5 pr-3 pl-3.5">
            <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap text-fg-muted">{row.instructions}</p>
          </blockquote>
          <p className="mt-1.5 text-[12px] leading-snug text-fg-subtle">{t.detail.instructionsHint}</p>
        </Panel>
      )}

      <CapabilityReview systemId={row.id} systemName={displayName} />
    </section>
  )
}

// ---------------------------------------------------------------------------
// Remote agents (PRD-26 GRP-061)
// ---------------------------------------------------------------------------

/**
 * The tab's second section: every registered remote agent, and who can use it.
 *
 * Registering one, reviewing its card and turning it on stay on the administration API — those
 * are judgements about a publisher, made with the card in front of you. What is decided here is
 * which of this platform's people may have their agents hand work to it, which is the same
 * decision as for a connected system and so looks exactly like it.
 */
const RemoteAgentsSection = ({ enabled }: { enabled: boolean }) => {
  const t = useT().admin.systems
  const agents = useRemoteAgents(enabled)
  const items = agents.data?.items ?? []

  return (
    <section className="mt-9 border-t border-rule pt-6">
      <SectionHead title={t.remote.title} lead={t.remote.lead} />

      {agents.isLoading && (
        <p className="mt-3 flex items-center gap-2 text-[13px] text-fg-subtle">
          <Spinner /> {t.remote.loading}
        </p>
      )}

      {agents.isError && (
        <div className="mt-3">
          <EmptyState title={t.remote.unavailable} body={t.remote.unavailableBody} />
        </div>
      )}

      {!agents.isLoading && !agents.isError && items.length === 0 && (
        <div className="mt-3">
          <EmptyState title={t.remote.empty} body={t.remote.emptyBody} />
        </div>
      )}

      <ul className="mt-4 space-y-2.5">
        {items.map((agent, index) => (
          <li key={agent.id}>
            <RemoteAgentRow agent={agent} index={index} onStale={() => void agents.refetch()} />
          </li>
        ))}
      </ul>
    </section>
  )
}

const RemoteAgentRow = ({ agent, index, onStale }: { agent: RemoteAgent; index: number; onStale: () => void }) => {
  const fmt = useI18n()
  const t = fmt.t.admin.systems
  const update = useUpdateRemoteAgent(agent.id)
  const name = agent.name || agent.slug

  return (
    <StudioRow style={riseDelay(index)}>
      <div className="flex flex-wrap items-start gap-x-2.5 gap-y-1.5">
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px]">
            {agent.name || t.remote.unnamed}
            <span className="readout ml-1.5 text-fg-subtle">{agent.slug}</span>
          </h3>
          {agent.description !== '' && (
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-fg-muted">{agent.description}</p>
          )}
        </div>
        {!agent.enabled && <Badge tone="alert">{t.remote.off}</Badge>}
      </div>

      <p className="mt-2 text-[12.5px] leading-snug text-fg-subtle">
        {/* No `?? agent.trustTier` any more: the map is typed against the contract enum, so a
            miss cannot happen and `first_party` can no longer reach the screen. */}
        {t.remote.trust[agent.trustTier]}
        <span aria-hidden className="mx-1.5">
          ·
        </span>
        {t.remote.skills(fmt, agent.skills.length)}
        {agent.cardFetchedAt && (
          <>
            <span aria-hidden className="mx-1.5">
              ·
            </span>
            {t.checked(fmt.formatAgo(agent.cardFetchedAt))}
          </>
        )}
      </p>
      {!agent.enabled && <p className="mt-1 text-[12.5px] leading-snug text-fg-muted">{t.remote.offHint}</p>}

      <div className="mt-3 border-t border-rule pt-3">
        <p className="eyebrow mb-2">{t.access.title}</p>
        <SystemAccessEditor
          name={name}
          access={agent.access}
          saving={update.isPending}
          onSave={(access, handlers) => update.mutate({ version: agent.version, patch: { access } }, handlers)}
          onStale={onStale}
        />
      </div>
    </StudioRow>
  )
}
